import React, { useEffect, useState } from "react";
import { Card, Skeleton, Table } from "antd";
import { useAccount } from "src/hooks/account.hook";
import { ProductResponse } from "src/models/account";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { accountAtom } from "src/store";
import { usePageTracking } from "src/hooks/tracking-page.hook";
import { ArrowDownOutlined, ArrowUpOutlined, EyeOutlined } from "@ant-design/icons";
import { Tag } from "antd";
import { Link } from "react-router-dom";
import { routes } from "src/data/routes";
import * as FormatUtils from "../utils/format"

export default function Products() {
  const { getProductLists } = useAccount();
  const [productsLoading, setProductsLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<any>(null);
  const [products, setProducts] = useState<ProductResponse[]>([]);
  const { t } = useTranslation("products");
  const [account] = useAtom(accountAtom);
  const { trackPage } = usePageTracking();

  useEffect(() => {
    getProducts();
  }, [account]);

  const getProducts = () => {
    setProductsLoading(true);
    setProducts([]);
    getProductLists({ userId: account?.id })
      .then((value) => setProducts(value))
      .finally(() => setProductsLoading(false));
  };

  useEffect(() => {
    if (pagination) {
      trackPage(pagination);
    }
  }, [pagination]);

  useEffect(() => {
    if (products.length > 0) {
      setPagination({
        page_number: 1,
        pages_total: products.length,
      });
    }
  }, [products]);

  const productListColumns = [
    {
      title: t('name'),
      dataIndex: 'product_name',
      key: 'product_name',
    },
    {
      title: t('productType'),
      dataIndex: 'product_type',
      key: 'product_type',
    },
    {
      title: t('isinCode'),
      dataIndex: 'isin_code',
      key: 'isin_code',
    },
    {
      title: t('shares'),
      dataIndex: 'shares',
      key: 'shares',
    },
    {
      title: t('capitalBalance', { ns: 'common' }),
      key: 'capital_balance',
      render: (_: any, record: any) => (
        <>{FormatUtils.currency.format(record?.capital_balance)}</>
      ),
    },

    {
      title: t('unrealisedInterest', { ns: 'common' }),
      key: 'unrealised_interest',
      dataIndex: 'unrealised_interest',
      render: (_: any, { unrealised_interest }: any) => {
        let color = unrealised_interest > 0 ? 'green' : 'volcano';
        let icon = unrealised_interest > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />;

        return (
          <Tag color={color} key={unrealised_interest} >
            {FormatUtils.currency.format(unrealised_interest)}
            {icon}
          </Tag >
        );
      }
    },
    {
      title: '',
      key: 'action',
      render: (_: any, record: any) => (
        record?.product_type == 'Collective Investment Scheme' ?
          <Link to={routes.PRODUCT_DETAIL} state={{ product: record }} >
            <EyeOutlined />
          </Link> : <></>
      ),
    },
  ];
  return (
    <>
      <div>
        <Card
          hoverable
          className="flex justify-center items-center mt-10 w-full bg-op"
        >
          <div className="flex justify-between w-full">
            <h2 className="text-center text-lg lg:text-2xl font-bold">
              {t("productListLabel")}
            </h2>
          </div>
          <div className="w-full flex justify-center w-full mt-5">
            {productsLoading ? (
              <Skeleton paragraph={{ rows: 4 }} />
            ) : (
              <Table
                columns={productListColumns}
                dataSource={products}
                className="w-full bg-op"
                rowClassName="bg-op"
                rootClassName="bg-op"
                scroll={{ x: 1000, y: 300 }}
                onChange={(pagination) => {
                  setPagination({
                    page_number: pagination?.current,
                    pages_total: products.length,
                  });
                }}
              />
            )}
          </div>
        </Card>
      </div>
    </>
  );
}
